/**
 * Labeled evidence list (supportive or contradictory) for an evaluation item.
 *
 * `DocChunk#NN` references are kept inline as clickable chips that deep-link the
 * left-hand clinical PDF to the cited chunk's page. In incorrect-evidence
 * feedback mode the list becomes selectable via checkboxes.
 * @module EvidenceSection
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { EvidenceFact } from "../../../data-model/nccnGuidelines";
import { usePdfNavigation } from "../context/PdfNavigationContext";
import { ChunkLocation } from "../utils/evidenceText";
import { VerdictColor, VERDICT_COLORS } from "../utils/verdictColors";
import { EvidenceText } from "./EvidenceText";

/** Visual tone of the section heading. */
type Tone = "supportive" | "contradictory";

/** Tag background/text colours keyed by section tone. */
const TONE_TAG_COLORS: Record<Tone, VerdictColor> = {
  supportive: VERDICT_COLORS.met,
  contradictory: VERDICT_COLORS.notMet,
};

/** Props for {@link EvidenceSection}. */
interface EvidenceSectionProps {
  title: string;
  tone: Tone;
  facts: EvidenceFact[];
  selectable?: boolean;
  selected?: string[];
  onToggle?: (fact: string) => void;
  /** Per-evidence correction text keyed by fact text (incorrect-evidence mode). */
  corrections?: Record<string, string>;
  /** Called when the reviewer edits a flagged fact's correction. */
  onCorrectionChange?: (fact: string, value: string) => void;
  /**
   * Chunk-location lookup for this item's `chunks_used`, so inline `DocChunk#N`
   * references resolve to a clickable page. Absent when the item has none.
   */
  locations?: Map<number, ChunkLocation>;
}

/**
 * Styling for the inline per-evidence correction `<input>` shown beneath a
 * checked fact when capturing incorrect-evidence feedback.
 */
const CORRECTION_INPUT_CLASS =
  "flex h-[1.6875rem] w-full items-center gap-[0.625rem] rounded-[0.25rem] " +
  "border border-[#D6D6D6] bg-white px-[0.75rem] text-[0.625rem] font-normal " +
  "leading-[1rem] text-[#6B6B6B] outline-none [font-family:Lato] " +
  "[font-feature-settings:'ss01'] [font-kerning:none] " +
  "placeholder:text-[#6B6B6B] focus:border-[#0F0F0F]";

/**
 * Renders a titled list of evidence facts with optional selection checkboxes.
 *
 * @param props - Title, tone, facts, and optional selection controls
 * @returns The rendered section, or null when there are no facts
 */
export const EvidenceSection: React.FC<EvidenceSectionProps> = ({
  title,
  tone,
  facts,
  selectable = false,
  selected = [],
  onToggle,
  corrections = {},
  onCorrectionChange,
  locations,
}) => {
  const { goToPage } = usePdfNavigation();
  if (!facts.length) return null;
  const tagColors = TONE_TAG_COLORS[tone];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex">
        <Tags
          label={title}
          backgroundColor={tagColors.background}
          textColor={tagColors.text}
        />
      </div>
      <ul className="flex flex-col items-start gap-[0.75rem] self-stretch">
        {facts.map((fact, index) => {
          const isSelected = selected.includes(fact.text);
          return (
            <li
              key={index}
              className="flex flex-col items-start gap-[0.5rem] self-stretch"
            >
              <div className="flex items-center gap-[0.625rem] self-stretch">
                {selectable && (
                  <input
                    type="checkbox"
                    className="shrink-0 accent-black"
                    style={{
                      width: "0.75rem",
                      height: "0.75rem",
                      borderRadius: "0.125rem",
                      border: "0.5px solid #3D3D3D",
                      background: "#FFF",
                    }}
                    checked={isSelected}
                    onChange={() => onToggle?.(fact.text)}
                  />
                )}
                <span className="text-[0.6875rem] font-normal leading-[1.03125rem] text-[#0F0F0F] [font-family:Lato]">
                  <EvidenceText
                    text={fact.text}
                    locations={locations}
                    onNavigate={goToPage}
                  />
                </span>
              </div>
              {selectable && isSelected && (
                <div className="self-stretch pl-[1.375rem]">
                  <input
                    type="text"
                    className={CORRECTION_INPUT_CLASS}
                    placeholder="Enter Correct Evidence"
                    value={corrections[fact.text] ?? ""}
                    onChange={(event) =>
                      onCorrectionChange?.(fact.text, event.target.value)
                    }
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
